<template>
  <div class="columns is-centered is-multiline">
    <div class="column is-two-thirds">
      <div class="field has-addons">
        <div class="control is-expanded">
          <input
            class="input"
            :class="{ 'is-danger': inviteError }"
            type="email"
            required="required"
            ref="guestInput"
            placeholder="Type an email here"
            :disabled="disableInvite"
            @keyup="validateEmail"
            @keyup.enter="onEnterKey"
            v-model="guest"
          />
        </div>
        <div class="control">
          <button
            class="button is-primary"
            :disabled="disableInviteButton"
            @click="inviteUser"
          >
            <span class="icon mr-1">
              <i class="fas fa-plus"></i>
            </span>
            Invite
          </button>
        </div>
      </div>
      <p class="help is-danger" v-show="inviteError">
        <small>{{ inviteError }}</small>
      </p>
    </div>
    <div class="column is-two-thirds">
      <p class="notification is-info">
        <strong>NOTE:</strong>
        Invitations expire 24 hours after they have been sent. You can always
        re-send an invitation by adding the email in the field above and
        clicking "+ Invite".
      </p>
    </div>
    <!-- Team members table -->
    <div class="column is-fullwidth">
      <table
        class="table is-bordered is-rounded is-striped is-info is-fullwidth mb-0"
      >
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th v-if="isOwner">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(user, index) in team" :key="user._id">
            <td>{{ user.name }}</td>
            <td>{{ user.email }}</td>
            <td>{{ user.role }}</td>
            <td>{{ user | status }}</td>
            <td class="has-text-centered" v-if="isOwner">
              <button
                class="button is-danger is-inverted tooltip is-tooltip-bottom"
                data-tooltip="Remove user"
                @click="openDeleteConfirmation($event, user, index)"
                v-if="canRemove(user)"
              >
                <span class="icon">
                  <i class="fas fa-trash"></i>
                </span>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="has-text-right has-text-grey px-1">
        <small>{{ team.length }}/{{ totalSeats }}</small>
      </p>
    </div>
    <!-- Confirmation Dialog -->
    <sdv-dialog v-model="confirmRemoval">
      <template #header>
        <p class="modal-card-title">Confirm Remove</p>
      </template>
      <template #footer>
        <button @click="removeUser" class="button is-danger">Remove</button>
      </template>
      <template>
        <h5 class="title is-size-5" style="margin: 0.75rem 0">
          Are you sure you want to remove {{ userToRemove }} from the team?
        </h5>
      </template>
    </sdv-dialog>
  </div>
</template>
<script>
export default {
  name: 'TeamForm',
  components: {
    SdvDialog: () => import('@/components/shared/SdvDialog')
  },
  props: ['userData'],
  async beforeMount() {
    this.team = await this.getTeam();
  },
  computed: {
    currentUser() {
      return this.$store.getters['auth/getUser'];
    },
    totalSeats() {
      let accessLevel = this.$store.getters['auth/getAccessLevel'];
      return accessLevel?.users?.value || 1;
    },
    disableInvite() {
      return this.team.length >= this.totalSeats;
    },
    isOwner() {
      return this.currentUser._id === this.currentUser.account.owner;
    },
    disableInviteButton() {
      return (
        this.disableInvite ||
        this.inviteError ||
        !this.$emailPattern.test(this.guest)
      );
    },
    userToRemove() {
      return this.toDelete?.user?.email || 'N/A';
    }
  },
  data() {
    return {
      loading: false,
      confirmRemoval: false,
      inviteError: null,
      toDelete: null,
      guest: null,
      team: []
    };
  },
  filters: {
    status(user) {
      return user.invitation?.pending ? 'Pending' : 'Accepted';
    }
  },
  methods: {
    async getTeam() {
      try {
        const response = await this.$axios.get(
          this.$API.teams(this.currentUser.account._id)
        );
        return response.data;
      } catch (ex) {
        console.error('Error fetching team: ', ex);
        return [this.currentUser];
      }
    },
    canRemove(user) {
      const isOwner = user._id === this.currentUser.account.owner;
      const isSame = user._id === this.currentUser._id;
      // If either one of the properties is true, the user cannot be removed
      return !(isSame || isOwner);
    },
    validateEmail() {
      this.$refs.guestInput.checkValidity();
      this.inviteError = this.$refs.guestInput?.validationMessage;
      if (!this.inviteError && !this.$emailPattern.test(this.guest)) {
        this.inviteError = `${this.guest} is not a valid email.`;
      }
      return !this.inviteError;
    },
    onEnterKey() {
      if (this.validateEmail()) {
        this.inviteUser();
      }
    },
    async inviteUser() {
      try {
        const response = await this.$axios.put(
          this.$API.teams(this.currentUser.account._id),
          {
            guest: this.guest
          }
        );
        const invitedUser = response.data;
        this.$notify({
          group: 'alert',
          title: 'SUCCESS!',
          text: `The user ${this.guest} has been added to your team.`,
          type: 'success'
        });
        this.guest = null;
        const existsIndex = this.team.findIndex(
          u => invitedUser.email === u.email
        );
        if (existsIndex > -1) {
          this.team[existsIndex] = invitedUser;
        } else {
          this.team.push(invitedUser);
        }
      } catch (ex) {
        console.error('Error inviting a user to the team: ', ex);
        this.$notify({
          group: 'alert',
          title: 'FAILED!',
          text: ex.response?.data || ex,
          type: 'error'
        });
      }
    },
    openDeleteConfirmation(evt, user, index) {
      evt.preventDefault();
      this.toDelete = {
        user,
        index
      };
      this.confirmRemoval = true;
    },
    async removeUser() {
      this.team.splice(this.toDelete.index, 1);
      this.confirmRemoval = false;
      try {
        await this.$axios.delete(
          this.$API.teams(this.currentUser.account._id, this.toDelete.user._id)
        );
        this.$notify({
          group: 'alert',
          title: 'SUCCESS!',
          text: `The user ${this.toDelete.user.email} has been removed from your team.`,
          type: 'success'
        });
      } catch (error) {
        console.error('Error inviting a user to the team: ', error);
        this.$notify({
          group: 'alert',
          title: 'FAILED!',
          text: error.response?.data?.message || error,
          type: 'error'
        });
        // Add the user back to the collection as they were not removed.
        this.team.splice(this.toDelete.index, 0, this.toDelete.user);
      } finally {
        // Clear the user to be deleted.
        this.toDelete = null;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.navbar {
  width: 100%;
  .navbar-item img {
    max-height: 1.75rem;
  }
}
.preview-container {
  margin-top: 1rem;
  .column {
    &.is-logo {
      img {
        width: 50%;
        height: auto;
      }
    }
    img {
      width: 100%;
      height: auto;

      &.watermark-preview {
        opacity: 0.15;
      }
    }
  }
}

.vc-chrome[disabled] {
  pointer-events: none;
  opacity: 0.5;
}

.file.disabled {
  pointer-events: none;
  opacity: 0.5;

  .file-input {
    display: none;
  }
}
.table {
  border-collapse: separate !important;
  border-spacing: 0;
  border-radius: 0.5rem;

  &.is-rounded {
    th:first-child {
      border-top-left-radius: 0.5rem;
    }
    th:last-child {
      border-top-right-radius: 0.5rem;
    }
    tbody {
      tr:last-child {
        td:first-child {
          border-bottom-left-radius: 0.5rem;
        }
        td:last-child {
          border-bottom-right-radius: 0.5rem;
        }
      }
    }
  }
}
</style>
